import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseAppSyncService } from '../base-app-sync.service';
import { queryForOpAlertLogs } from '../../graphql/field-operations/query-for-op-alert-logs';
import { updateOpAlertLog } from '../../graphql/field-operations/update-op-alert-log';
import { getOpAlertLog } from '../../graphql/field-operations/get-op-alert-log';
import { createOpAlertLogEntry } from '../../graphql/field-operations/create-op-alert-log-entry';
import { updateOpAlertLogEntry } from '../../graphql/field-operations/update-op-alert-log-entry';
import { queryForAlarmLimits } from '../../graphql/field-operations/query-for-alarm-limits';
import { createAlarmLimit } from '../../graphql/field-operations/create-alarm-limit';
import { deleteAlarmLimit } from '../../graphql/field-operations/delete-alarm-limit';
import { updateAlarmLimit } from '../../graphql/field-operations/update-alarm-limit';
import { getAlarmLimitHistories } from '../../graphql/field-operations/get-alarm-limit-histories';
import { sendAlarmLimitRequestEmail } from '../../graphql/field-operations/send-alarm-limit-request-email';
import { getAlarmLimits } from '../../graphql/field-operations/get-alarm-limits';
import { getPIConfigurableElements } from '../../graphql/field-operations/get-pi-configurable-elements';
import { getLatestPIValue } from '../../graphql/field-operations/get-latest-pi-value';
import { PIValue } from '../../model/operational-monitoring/pi-value';
import { OpAlertLogsBuilderService } from '../builder/op-alert-logs-builder.service';
import { getPIValuePlot } from '../../graphql/field-operations/get-pi-value-plot';
import {
  AlarmLimit,
  AlarmLimitConnection,
  AlarmLimitInput,
  AlarmLimitSortOrQuery,
} from 'src/app/model/operational-monitoring/alarm-limit';
import { PiConfigurableElement } from 'src/app/model/operational-monitoring/pi-configurable-element';
import { AlarmLimitHistory } from 'src/app/model/operational-monitoring/alarm-limit-history';
import {
  OpAlertLog,
  OpAlertLogSortOrQuery,
} from 'src/app/model/opAlertLogs/op-alert-log';
import { OpAlertLogEntry } from 'src/app/model/opAlertLogs/op-alert-log-entry';
import { OpAlertLogEntryInput } from 'src/app/model/opAlertLogs/op-alert-log-entry-input';
import { OpAlertLogInput } from 'src/app/model/opAlertLogs/op-alert-log-input';

@Injectable({
  providedIn: 'root',
})
export class OperationalMonitoringApiService extends BaseAppSyncService {
  private _opBuilder = inject(OpAlertLogsBuilderService);

  queryForOpAlertLogs(
    limit: number,
    page: number,
    query: OpAlertLogSortOrQuery,
    sort: OpAlertLogSortOrQuery
  ): Observable<any> {
    return this.query(
      queryForOpAlertLogs,
      { limit, page, query, sort },
      (i: any) => this._opBuilder.buildOpAlertLogConnection(i),
      'queryForOpAlertLogs'
    );
  }

  getOpAlertLog(id: string): Observable<OpAlertLog> {
    return this.query(
      getOpAlertLog,
      { id },
      (i: any) => this._opBuilder.buildOpAlertLog(i),
      'getOpAlertLog'
    );
  }

  updateOpAlertLog(
    log: OpAlertLog,
    logInput: OpAlertLogInput
  ): Observable<OpAlertLog> {
    return this.mutate(
      updateOpAlertLog,
      { id: log.id, input: logInput },
      (i: any) => this._opBuilder.buildOpAlertLog(i),
      'updateOpAlertLog'
    );
  }

  createOpAlertLogEntry(
    logInput: OpAlertLogEntryInput
  ): Observable<OpAlertLogEntry> {
    return this.mutate(
      createOpAlertLogEntry,
      { input: logInput },
      (i: any) => this._opBuilder.buildOpAlertLogEntry(i),
      'createOpAlertLogEntry'
    );
  }

  updateOpAlertLogEntry(
    logInput: OpAlertLogEntryInput
  ): Observable<OpAlertLogEntry> {
    return this.mutate(
      updateOpAlertLogEntry,
      { input: logInput },
      (i: any) => this._opBuilder.buildOpAlertLogEntry(i),
      'updateOpAlertLogEntry'
    );
  }

  queryForAlarmLimits(
    limit: number,
    page: number,
    query: AlarmLimitSortOrQuery,
    sort: AlarmLimitSortOrQuery
  ): Observable<AlarmLimitConnection> {
    return this.query(
      queryForAlarmLimits,
      { limit, page, query, sort },
      (i: any) => this._opBuilder.buildAlarmLimitConnection(i),
      'queryForAlarmLimits'
    );
  }

  createAlarmLimit(alarmLimitInput: AlarmLimitInput): Observable<AlarmLimit> {
    return this.mutate(
      createAlarmLimit,
      { input: alarmLimitInput },
      (i: any) => this._opBuilder.buildAlarmLimit(i),
      'createAlarmLimit'
    );
  }

  deleteAlarmLimit(id: string): Observable<boolean> {
    return this.mutate(
      deleteAlarmLimit,
      { id },
      (i: any) => i,
      'deleteAlarmLimit'
    );
  }

  updateAlarmLimit(
    id: string,
    alarmLimitInput: AlarmLimitInput
  ): Observable<AlarmLimit> {
    return this.mutate(
      updateAlarmLimit,
      { id, input: alarmLimitInput },
      (i: any) => this._opBuilder.buildAlarmLimit(i),
      'updateAlarmLimit'
    );
  }

  getAlarmLimitHistories(parentId: string): Observable<AlarmLimitHistory[]> {
    return this.query(
      getAlarmLimitHistories,
      { parentId },
      (items: any[]) =>
        items.map((i) => this._opBuilder.buildAlarmLimitHistory(i)),
      'getAlarmLimitHistories'
    );
  }

  sendAlarmLimitRequestEmail(alarmLimitRequest: any): Observable<boolean> {
    return this.mutate(
      sendAlarmLimitRequestEmail,
      { input: alarmLimitRequest },
      (i: any) => i.value,
      'sendAlarmLimitRequestEmail'
    );
  }

  getAlarmLimits(): Observable<AlarmLimit[]> {
    return this.query(
      getAlarmLimits,
      {},
      (items: any[]) => items.map((i) => this._opBuilder.buildAlarmLimit(i)),
      'getAlarmLimits'
    );
  }

  getPIConfigurableElements(): Observable<PiConfigurableElement[]> {
    return this.query(
      getPIConfigurableElements,
      {},
      (items: any[]) =>
        items.map((i) => this._opBuilder.buildPiConfigurableElement(i)),
      'getPIConfigurableElements'
    );
  }

  getLatestPIValue(piPoint: string): Observable<PIValue> {
    return this.query(
      getLatestPIValue,
      { piPoint },
      (i: any) => this._opBuilder.buildPIValue(i),
      'getLatestPIValue'
    );
  }

  getPIValuePlot(piPoint: string, hours: number = 3): Observable<PIValue[]> {
    return this.query(
      getPIValuePlot,
      { piPoint, hours },
      (items: any) =>
        items.map((item: any) => this._opBuilder.buildPIValue(item)),
      'getPIValuePlot'
    );
  }
}
